/**
 * Client-Level Summary Utilities
 *
 * Shared utilities for client-level summary sections in proposals.
 * Provides view-based filtering, portfolio selection, and data aggregation
 * that can be reused across multiple proposal elements (account breakdown,
 * asset allocation, performance charts, etc.).
 */

import * as ProposalNS from '../Proposal';
import ComparisonItem from '@/types/comparison-item';
import ComparisonHolding from '@/types/comparison-holding';
import PercentGrowth from '@/types/percent-growth';
import GrowthTypes from '@/types/growth-types';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';
import LocalizedString from '@/types/localized-string';
import AllocationDetails from '@/types/allocation-details';
import Holdings from '@/types/holdings';
import { LOCALE } from '@/utils/i18n';

/**
 * Gets portfolios for a specific view, including primary recommendation logic
 *
 * For CURRENT view:
 * - Returns current portfolios from account sets (currentItemId)
 * - Only includes items that are in the proposal.items array
 *
 * For RECOMMENDED view:
 * - Returns primary recommended portfolios from account sets
 * - Only includes items marked as primary recommendations
 *
 * @param proposal - The proposal object
 * @param view - View type (CURRENT or RECOMMENDED)
 * @returns Array of comparison items (portfolios)
 *
 * @example
 * const currentPortfolios = getPortfoliosForView(proposal, CLIENT_LEVEL_VIEW.CURRENT);
 * const recommendedPortfolios = getPortfoliosForView(proposal, CLIENT_LEVEL_VIEW.RECOMMENDED);
 */
export const getPortfoliosForView = (proposal: ProposalNS.Proposal, view: CLIENT_LEVEL_VIEW): ComparisonItem[] => {
  const { accountSets } = proposal;

  if (!accountSets || accountSets.length === 0) {
    return [];
  }
  if (view === CLIENT_LEVEL_VIEW.CURRENT) {
    // Get all current item IDs from account sets
    const currentItemIds = new Set<number>();
    accountSets.forEach(accountSet => {
      if (accountSet.current !== undefined && accountSet.current >= 0) {
        currentItemIds.add(accountSet.current);
      }
    });

    // Filter items that are in the current item list
    return proposal.items.filter((item, index) => currentItemIds.has(index));
  }

  // RECOMMENDED view: Get primary recommended portfolios from account sets
  const primaryRecommendedItemIds = new Set<number>();
  accountSets.forEach(accountSet => {
    accountSet.recommendations.forEach(rec => {
      if (rec.isPrimary && rec.itemId >= 0) {
        primaryRecommendedItemIds.add(rec.itemId);
      }
    });
  });

  // Filter items that are in the primary recommended list
  return proposal.items.filter((item, index) => primaryRecommendedItemIds.has(index));
};

/**
 * Gets the portfolio value
 *
 * For groups: Sums the dollars from all groupItems
 * For regular portfolios: Uses size value
 *
 * @param portfolio - Comparison item (portfolio or group)
 * @returns Portfolio value as number
 */
export const getPortfolioValue = (portfolio: ComparisonItem): number => {
  // For groups, calculate total value from groupItems
  if (portfolio.type === 'group' && portfolio.groupItems && portfolio.groupItems.length > 0) {
    return portfolio.groupItems.reduce((sum, item) => sum + (item.dollars || 0), 0);
  }

  // For regular portfolios, use size
  return portfolio.size || 0;
};

/**
 * Calculates total value across multiple portfolios
 *
 * @param portfolios - Array of portfolios
 * @returns Sum of all portfolio values
 *
 * @example
 * const portfolios = getPortfoliosForView(proposal, CLIENT_LEVEL_VIEW.CURRENT);
 * const totalValue = calculateTotalPortfolioValue(portfolios);
 */
export const calculateTotalPortfolioValue = (portfolios: ComparisonItem[]): number =>
  portfolios.reduce((sum, portfolio) => sum + getPortfolioValue(portfolio), 0);

/**
 * Calculate weight of a portfolio relative to total value
 */
export const calculatePortfolioWeight = (portfolioValue: number, totalValue: number): number => {
  return totalValue > 0 ? portfolioValue / totalValue : 0;
};

/**
 * Aggregate detailedHoldings from portfolios (recalculates weights based on portfolio values)
 * Uses the existing detailedHoldings.list from each portfolio, which contains
 * individual securities/instruments, not portfolio-level items.
 *
 * This matches the data source used by single-page groups, which get their
 * top holdings from detailedHoldings.list (populated by calculatePortfolioStats).
 *
 * @example
 * Portfolio A ($100k) has detailedHoldings.list with "Canada Govt Bond" at 10% = $10k
 * Portfolio B ($200k) has detailedHoldings.list with "Canada Govt Bond" at 8% = $16k
 * → Aggregated weight = ($10k + $16k) / $300k * 100 = 8.67%
 */
export const aggregateDetailedHoldings = (portfolios: ComparisonItem[], totalValue: number): Holdings => {
  const holdingsMap = new Map<string, { name: string; dollars: number }>();

  portfolios.forEach(portfolio => {
    const portfolioValue = getPortfolioValue(portfolio);

    const detailedHoldingsList = portfolio.detailedHoldings?.list || [];

    detailedHoldingsList.forEach(holding => {
      const holdingDollars = (holding.weight * portfolioValue) / 100;

      const existing = holdingsMap.get(holding.name);
      if (existing) {
        existing.dollars += holdingDollars;
      } else {
        holdingsMap.set(holding.name, { name: holding.name, dollars: holdingDollars });
      }
    });
  });

  // Convert dollar values to weights as percentage of total
  const detailedHoldingsList: AllocationDetails[] = Array.from(holdingsMap.values())
    .map(({ name, dollars }) => ({
      name,
      weight: totalValue > 0 ? (dollars / totalValue) * 100 : 0,
    }))
    .sort((a, b) => b.weight - a.weight);

  return {
    list: detailedHoldingsList,
  };
};

/**
 * Sum holding values from portfolios (recalculates weights based on dollar values)
 * @example
 * Portfolio A ($100k): Holding X at 50% = $50k
 * Portfolio B ($200k): Holding X at 40% = $80k
 * Combined ($300k): Holding X = ($50k + $80k) / $300k = 43.3%
 */
export const recalculateHoldingsWeights = (portfolios: ComparisonItem[], totalValue: number): ComparisonHolding[] => {
  const combinedHoldings: ComparisonHolding[] = [];

  portfolios.forEach(portfolio => {
    const portfolioValue = getPortfolioValue(portfolio);

    if (portfolio.holdings && portfolio.holdings.length > 0) {
      portfolio.holdings.forEach(holding => {
        const holdingDollars = (holding.weight * portfolioValue) / 100;
        const aggregatedWeight = totalValue > 0 ? (holdingDollars / totalValue) * 100 : 0;

        combinedHoldings.push({
          ...holding,
          weight: aggregatedWeight,
        });
      });
    }
  });

  return combinedHoldings;
};

/**
 * Aggregate asset allocations (weighted by portfolio value)
 */
export const aggregateAssetAllocations = (portfolios: ComparisonItem[], totalValue: number): { [key: string]: number } => {
  const result: { [key: string]: number } = {};

  portfolios.forEach(portfolio => {
    const data = portfolio.allocations?.asset;
    if (!data) return;

    const weight = calculatePortfolioWeight(getPortfolioValue(portfolio), totalValue);

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        result[key] = (result[key] || 0) + value * weight;
      }
    });
  });

  return result;
};

/**
 * Aggregate country allocations (weighted by portfolio value)
 */
export const aggregateCountryAllocations = (portfolios: ComparisonItem[], totalValue: number): Array<{ name: string; weight: number }> => {
  const map = new Map<string, number>();

  portfolios.forEach(portfolio => {
    const items = portfolio.allocations?.country;
    if (!items) return;

    const weight = calculatePortfolioWeight(getPortfolioValue(portfolio), totalValue);

    items.forEach(item => {
      const existing = map.get(item.name) || 0;
      map.set(item.name, existing + item.weight * weight);
    });
  });

  return Array.from(map.entries())
    .map(([name, weight]) => ({ name, weight }))
    .sort((a, b) => b.weight - a.weight);
};

/**
 * Aggregate performance data (weighted by portfolio value)
 *
 * Aggregates percentGrowth (trailing returns) which can be weighted-averaged.
 * Does not aggregate complex risk metrics (standard deviation, correlation, etc.)
 * as they require portfolio theory calculations (covariance matrices).
 *
 * Note: This provides default performance data for initial proposal rendering
 * (overview tables, summary stats). Charts will recalculate from holdings.
 */
export const aggregatePerformance = (portfolios: ComparisonItem[], totalValue: number) => {
  if (portfolios.length === 0 || totalValue === 0) {
    return {};
  }

  // Aggregate percentGrowth (returns can be weighted-averaged)
  const aggregatedPercentGrowth: Partial<PercentGrowth> = {};

  portfolios.forEach(portfolio => {
    const weight = getPortfolioValue(portfolio) / totalValue;
    const percentGrowth = portfolio.performance?.percentGrowth;

    if (percentGrowth) {
      Object.entries(percentGrowth).forEach(([period, value]) => {
        if (value !== null && value !== undefined) {
          const key = period as GrowthTypes;
          aggregatedPercentGrowth[key] = (aggregatedPercentGrowth[key] || 0) + value * weight;
        }
      });
    }
  });

  // Return performance with aggregated percentGrowth
  // Risk metrics are omitted - they require complex calculations
  return {
    percentGrowth: aggregatedPercentGrowth as PercentGrowth,
  };
};

/**
 * Aggregate sector allocations (weighted by portfolio value)
 * Uses en-CA as the canonical key for aggregation while preserving
 * all locale translations from the first occurrence of each sector.
 */
export const aggregateSectorAllocations = (portfolios: ComparisonItem[], totalValue: number) => {
  const map = new Map<string, { name: LocalizedString; weight: number }>();

  portfolios.forEach(portfolio => {
    const items = portfolio.allocations?.sector;
    if (!items) return;

    const weight = calculatePortfolioWeight(getPortfolioValue(portfolio), totalValue);

    items.forEach(item => {
      const key = item.name[LOCALE.EN_CA];
      if (!key) return;

      const existing = map.get(key);
      const newWeight = (existing?.weight || 0) + item.weight * weight;

      map.set(key, {
        name: existing?.name || item.name, // Preserve first occurrence's translations
        weight: newWeight,
      });
    });
  });

  return Array.from(map.values()).sort((a, b) => b.weight - a.weight);
};

/**
 * Aggregate calendar year returns (weighted by portfolio value)
 * Calendar year returns can be weighted-averaged like trailing returns
 *
 * @returns Dictionary of year -> weighted average return
 * @example { '2024': 11.23, '2023': 1.03, '2022': 3.50, '2021': 8.15, '2020': 6.23 }
 */
export const aggregateCalendarYearReturns = (portfolios: ComparisonItem[], totalValue: number): { [year: string]: number } | undefined => {
  if (portfolios.length === 0 || totalValue === 0) {
    return undefined;
  }

  const aggregatedReturns: { [year: string]: number } = {};

  portfolios.forEach(portfolio => {
    const weight = getPortfolioValue(portfolio) / totalValue;
    const calendarYear = portfolio.performance?.calendarYear;

    if (calendarYear) {
      Object.entries(calendarYear).forEach(([year, value]) => {
        if (value !== null && value !== undefined) {
          aggregatedReturns[year] = (aggregatedReturns[year] || 0) + value * weight;
        }
      });
    }
  });

  return Object.keys(aggregatedReturns).length > 0 ? aggregatedReturns : undefined;
};

/**
 * Aggregate MER fields (weighted by portfolio value)
 * Calculates weighted-average MER, discretionary MER (advisory fee), and tax
 *
 * @returns Aggregated MER fields
 * @example { mer: 1.20, discretionaryMer: 0.75, tax: undefined }
 */
export const aggregateMER = (portfolios: ComparisonItem[], totalValue: number): { mer?: number; discretionaryMer?: number; tax?: number } => {
  if (portfolios.length === 0 || totalValue === 0) {
    return {};
  }

  let aggregatedMER = 0;
  let aggregatedDiscretionaryMER = 0;
  let aggregatedTax = 0;
  let hasMER = false;
  let hasDiscretionaryMER = false;
  let hasTax = false;

  portfolios.forEach(portfolio => {
    const weight = getPortfolioValue(portfolio) / totalValue;

    if (portfolio.mer !== null && portfolio.mer !== undefined) {
      aggregatedMER += portfolio.mer * weight;
      hasMER = true;
    }

    if (portfolio.discretionaryMer !== null && portfolio.discretionaryMer !== undefined) {
      aggregatedDiscretionaryMER += portfolio.discretionaryMer * weight;
      hasDiscretionaryMER = true;
    }

    if (portfolio.tax !== null && portfolio.tax !== undefined) {
      aggregatedTax += portfolio.tax * weight;
      hasTax = true;
    }
  });

  return {
    mer: hasMER ? aggregatedMER : undefined,
    discretionaryMer: hasDiscretionaryMER ? aggregatedDiscretionaryMER : undefined,
    tax: hasTax ? aggregatedTax : undefined,
  };
};

/**
 * Creates a synthetic aggregated portfolio item from multiple portfolios
 *
 * This creates a **simplified** ComparisonItem that represents the weighted aggregation
 * of all input portfolios. The resulting item can be passed to existing DAOs
 * (AssetAllocationDAO, DetailedHdingsDAO, HypotheticalReturnDAO, OverviewDAO, etc.)
 * which will process it normally.
 *
 *  **LIMITATIONS**
 * This function creates a MINIMAL aggregated portfolio with only essential fields populated.
 * It does NOT run the full portfolio calculation that `combineProductsIntoGroupItem` uses.
 *
 * Fields populated:
 * - _id, name, type, size, currency
 * - holdings: Weighted and recalculated based on dollar values
 * - allocations: asset, country, sector (weighted averages)
 * - performance.percentGrowth: Weighted average of trailing returns (1Y, 3Y, 5Y, etc.)
 * - performance.calendarYear: Weighted average of calendar year returns (2024, 2023, etc.)
 * - mer, discretionaryMer, tax: Weighted average of fee fields
 * - dates.allocations: Most recent date from all portfolios
 * - riskProfile: From first portfolio (fallback)
 *
 * Fields NOT populated and implementation requirements:
 *
 * **Performance Time-Series Fields** (requires async calculation):
 * - performance.rolling, rollingUSD, riskFreeRolling:
 *   → Requires time-series calculations from daily/weekly/monthly price data
 *   → Must call calculatePortfolioStats() with aggregated holdings
 *   → Needs historical price data for each underlying instrument
 *   → Implementation: Use combineProductsIntoGroupItem + populatePortfolioMetrics
 *
 * - performance.calendarYear, bestWorst, upDownMonths:
 *   → Derived from rolling data (needs rolling calculated first)
 *   → calendarYear: AGGREGATED via weighted average
 *   → bestWorst: Extract min/max from rolling data
 *   → upDownMonths: Count positive/negative months from rolling
 *   → Implementation: Use combineProductsIntoGroupItem (includes these calculations)
 *
 * **Risk Metrics** (requires covariance matrices):
 * - performance.risk.standardDeviation, correlation, sharpe, beta, alpha:
 *   → Requires covariance matrix calculations across holdings
 *   → Must calculate portfolio variance from weighted instrument correlations
 *   → Needs benchmark data for beta/alpha calculations
 *   → Implementation: Use combineProductsIntoGroupItem + populatePortfolioMetrics
 *
 * **Fee Fields** (simple weighted average):
 * - mer, discretionaryMer, tax:
 *   → Uses weightedAverage() primitive: sum(value * weight) / totalValue
 *   → Example: mer = portfolios.reduce((sum, p) => sum + (p.mer * p.size), 0) / totalValue
 * **Other Fields**:
 * - allocations.detailedAsset: Not needed for current use cases (asset is sufficient)
 * - benchmarks: Not applicable to aggregated portfolios
 * - disclaimers: Would need to merge from all portfolios (complex merge logic)
 * - detailedHoldings, source, enterprise*, portfolioType: Metadata fields
 * - characteristics, investmentInfo, scenarios: Advanced fields not needed for proposals
 * - All ESGInstrument, FlaggableInstrument, AvailableActions fields
 *
 * **Scope: Client Summary and Recommendation Summary (ADR-8)**
 *
 * This function is designed specifically for ADR-8 pages which display:
 * - Account Breakdown Table
 * - Asset Allocation, Top Holdings, Country, Equity Industry Donuts
 * - Fee Breakdown Table (MER, Advisory Fee, Total Cost)
 * - Calendar Year Returns Table (last 5 years)
 * - Trailing Returns Table (YTD, 6M, 1Y, 3Y, 5Y, 10Y)
 * - Performance Chart (growth over time)
 *
 * **Why Missing Fields Are Not Needed**:
 *
 * 1. **performance.rolling, rollingUSD, riskFreeRolling** (NOT populated):
 *    - We don't need this for the Performance Section because:
 *    - HypotheticalReturnDAO IGNORES item.performance.rolling completely!
 *    - Instead, it calls DataPoints.getHypotheticalReturnSeries() which:
 *      a) Takes the aggregated HOLDINGS from our ComparisonItem
 *      b) Calls mergeChartItems() to get instrument-level price data
 *      c) Calls getGraphData() to calculate daily/weekly/monthly returns from scratch
 *      d) Generates the line chart series from instrument prices, NOT from item.performance.rolling
 *    - This means the DAO recalculates the entire performance time series dynamically
 *    - Pre-populating performance.rolling would be wasteful - the DAO never reads it
 *    - The aggregated holdings are sufficient; the chart is computed from them at render time
 *
 * 2. **performance.bestWorst, upDownMonths** (NOT populated):
 *    - Best/worst performance and up/down month counts are NOT displayed in ADR-8
 *    - These fields are only needed for detailed risk analysis pages (Risk Profile, etc.)
 *    - Client Summary focuses on allocation and returns, not volatility metrics
 *
 * 3. **performance.risk (standardDeviation, correlation, sharpe, beta, alpha)** (NOT populated):
 *    - Risk metrics are NOT displayed in Client Summary or Recommendation Summary
 *    - These require covariance matrix calculations which can't be weighted-averaged
 *    - Risk/reward scatter plots (which need these) are only in Executive Summary (ADR-9)
 *    - ADR-8 scope is allocation and returns, not risk analysis
 *
 * 4. **allocations.detailedAsset** (NOT populated):
 *    - ADR-8 uses only top-level asset allocation (Cash, Fixed Income, Equity, Other)
 *    - Detailed sub-allocations not shown
 *    - Keeps donuts simple and focused for client-level summary
 *
 * 5. **benchmarks, disclaimers** (NOT populated):
 *    - Aggregated portfolios don't have associated benchmarks
 *    - Individual portfolio DAOs already handle their own disclaimers
 *    - Disclaimer merging is handled by DAO layer, not here
 *
 * This function is a **lightweight synchronous alternative** to `combineProductsIntoGroupItem`
 * which avoids Redux dispatch, async operations, and heavy calculations, making it suitable
 * for synchronous proposal rendering where only basic aggregation is needed.
 *
 * @param portfolios - Array of portfolios to aggregate
 * @param view - View type (CURRENT or RECOMMENDED)
 * @returns Simplified aggregated ComparisonItem with full allocation data
 *
 * @example
 * // Basic usage
 * const portfolios = getPortfoliosForView(proposal, CLIENT_LEVEL_VIEW.CURRENT);
 * const aggregated = createAggregatedPortfolio(portfolios, CLIENT_LEVEL_VIEW.CURRENT);
 *
 * // Access aggregated data
 * console.log(aggregated.allocations.sector);  // All sectors with weights
 * console.log(aggregated.performance.percentGrowth);  // { '1Y': 8.5, '3Y': 12.3, '5Y': 10.1 }
 *
 * // Get top N allocations (useful for donuts/tables)
 * const topSectors = getTopNSectors(aggregated, 5);
 * const topCountries = getTopNCountries(aggregated, 5);
 */
export const createAggregatedPortfolio = (portfolios: ComparisonItem[], view: CLIENT_LEVEL_VIEW): ComparisonItem => {
  const totalValue = calculateTotalPortfolioValue(portfolios);

  // Get the most recent allocations date
  const mostRecentDate = portfolios.reduce((latest, p) => {
    if (!p.dates?.allocations) return latest;
    if (!latest) return p.dates.allocations;
    return new Date(p.dates.allocations) > new Date(latest) ? p.dates.allocations : latest;
  }, portfolios[0]?.dates?.allocations);

  // Aggregate MER fields
  const merFields = aggregateMER(portfolios, totalValue);

  // Aggregate detailedHoldings from all portfolios
  // This uses the existing detailedHoldings.list from each portfolio,
  // which contains individual securities (same as single-page groups)
  const detailedHoldingsData: Holdings = aggregateDetailedHoldings(portfolios, totalValue);

  // For the holdings array, we still use the portfolio-level holdings
  // (this is used for some calculations, but NOT for top holdings display)
  const aggregatedHoldings: ComparisonHolding[] = recalculateHoldingsWeights(portfolios, totalValue);

  // Create synthetic aggregated portfolio
  return {
    _id: `aggregated-${view}`,
    name: {},
    type: 'portfolio',
    size: totalValue,
    currency: portfolios[0]?.currency,
    holdings: aggregatedHoldings,
    detailedHoldings: detailedHoldingsData,
    allocations: {
      asset: aggregateAssetAllocations(portfolios, totalValue),
      country: aggregateCountryAllocations(portfolios, totalValue),
      sector: aggregateSectorAllocations(portfolios, totalValue),
    },
    performance: {
      ...aggregatePerformance(portfolios, totalValue),
      calendarYear: aggregateCalendarYearReturns(portfolios, totalValue),
    },
    mer: merFields.mer,
    discretionaryMer: merFields.discretionaryMer,
    tax: merFields.tax,
    dates: { allocations: mostRecentDate },
    riskProfile: portfolios[0]?.riskProfile,
  };
};
